<template>
  <div class="tag-page">
    <section class="tag-stats">
      <article v-for="item in statCards" :key="item.key" class="tag-stat" :class="`tag-stat--${item.tone}`">
        <span>{{ item.label }}</span>
        <strong>{{ formatNumber(item.value) }}</strong>
        <small>{{ item.hint }}</small>
      </article>
    </section>

    <section class="tag-card">
      <div class="tag-toolbar">
        <div class="tag-tabs">
          <button
            v-for="tab in statusTabs"
            :key="tab.value || 'ALL'"
            type="button"
            :class="{ active: filters.status === tab.value }"
            @click="changeStatus(tab.value)"
          >{{ tab.label }} <em>{{ tab.count }}</em></button>
        </div>
        <button class="ghost-button" type="button" :disabled="rebuilding" @click="rebuildIndexes">
          {{ rebuilding ? "重建中…" : "重建标签索引" }}
        </button>
      </div>

      <div class="tag-filters">
        <input v-model.trim="filters.keyword" type="search" placeholder="搜索标签名称" @keyup.enter="search" />
        <button class="primary-button" type="button" @click="search">搜索</button>
      </div>

      <div v-if="message" class="tag-message" :class="{ 'tag-message--error': messageError }">{{ message }}</div>
      <div v-if="loading" class="tag-state">正在加载标签…</div>
      <div v-else-if="!rows.length" class="tag-state">没有匹配的标签</div>
      <div v-else class="tag-grid">
        <article v-for="item in rows" :key="item.normalizedName" class="tag-item" :class="{ 'tag-item--blocked': item.moderationStatus === 'BLOCKED' }">
          <div class="tag-item__head">
            <div class="tag-name">
              <span>#</span>{{ item.displayName }}
            </div>
            <span class="tag-quality">{{ item.status }}</span>
          </div>
          <div class="tag-normalized">{{ item.normalizedName }}</div>
          <dl class="tag-metrics">
            <div><dt>帖子</dt><dd>{{ item.postCount }}</dd></div>
            <div><dt>用户</dt><dd>{{ item.userCount }}</dd></div>
            <div><dt>互动</dt><dd>{{ item.interactionCount }}</dd></div>
            <div><dt>质量分</dt><dd>{{ Number(item.qualityScore || 0).toFixed(2) }}</dd></div>
          </dl>
          <div class="tag-item__foot">
            <span>最近使用 {{ formatTime(item.lastUsedAt) }}</span>
            <span v-if="item.moderationStatus === 'BLOCKED'" class="blocked-label">已屏蔽</span>
          </div>
          <div class="tag-actions">
            <button type="button" @click="openRename(item)">改名</button>
            <button type="button" @click="openMerge(item)">合并</button>
            <button type="button" :class="{ danger: item.moderationStatus !== 'BLOCKED' }" @click="toggleBlocked(item)">
              {{ item.moderationStatus === "BLOCKED" ? "恢复" : "屏蔽" }}
            </button>
          </div>
        </article>
      </div>

      <div v-if="total > pageSize" class="pager">
        <button class="ghost-button" type="button" :disabled="page <= 1" @click="changePage(page - 1)">上一页</button>
        <span class="pager-text">第 {{ page }} / {{ totalPages }} 页 · 共 {{ total }} 个标签</span>
        <button class="ghost-button" type="button" :disabled="page >= totalPages" @click="changePage(page + 1)">下一页</button>
      </div>
    </section>

    <div v-if="renameTag" class="tag-modal-mask" @click.self="closeRename">
      <form class="tag-modal" @submit.prevent="saveRename">
        <header>
          <div><small>修改展示名称</small><h2>#{{ renameTag.displayName }}</h2></div>
          <button type="button" @click="closeRename">×</button>
        </header>
        <label>
          <span>新名称</span>
          <input v-model.trim="renameValue" maxlength="64" placeholder="留空恢复系统名称" />
        </label>
        <p>只修改展示名称，不影响已经关联的帖子。</p>
        <footer>
          <button class="ghost-button" type="button" @click="closeRename">取消</button>
          <button class="primary-button" type="submit" :disabled="saving">保存</button>
        </footer>
      </form>
    </div>

    <div v-if="mergeSource" class="tag-modal-mask" @click.self="closeMerge">
      <form class="tag-modal" @submit.prevent="saveMerge">
        <header>
          <div><small>合并标签</small><h2>#{{ mergeSource.displayName }}</h2></div>
          <button type="button" @click="closeMerge">×</button>
        </header>
        <label>
          <span>合并到</span>
          <select v-model="mergeTarget">
            <option value="">请选择目标标签</option>
            <option
              v-for="item in mergeOptions"
              :key="item.normalizedName"
              :value="item.normalizedName"
            >#{{ item.displayName }}（{{ item.postCount }} 条帖子）</option>
          </select>
        </label>
        <p>合并后原标签下的帖子会转到目标标签，并建立别名关系。这个操作会重建标签索引。</p>
        <footer>
          <button class="ghost-button" type="button" @click="closeMerge">取消</button>
          <button class="primary-button" type="submit" :disabled="saving || !mergeTarget">确认合并</button>
        </footer>
      </form>
    </div>
  </div>
</template>

<script>
import {
  fetchSquareTagOverview,
  fetchSquareTags,
  mergeSquareTags,
  rebuildSquareTagIndexes,
  updateSquareTagDisplayName,
  updateSquareTagStatus
} from "../api/squareTagAdmin"

const emptyStats = () => ({
  totalTags: 0,
  activeTags: 0,
  emergingTags: 0,
  candidateTags: 0,
  blockedTags: 0,
  tagAssignments: 0,
  aliasCount: 0,
  relationCount: 0
})

export default {
  name: "SquareTagManageView",
  data() {
    return {
      loading: false,
      rebuilding: false,
      saving: false,
      stats: emptyStats(),
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      filters: { keyword: "", status: "" },
      renameTag: null,
      renameValue: "",
      mergeSource: null,
      mergeTarget: "",
      mergeOptions: [],
      message: "",
      messageError: false
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil((this.total || 0) / this.pageSize))
    },
    statCards() {
      return [
        { key: "total", label: "全部标签", value: this.stats.totalTags, hint: `${this.stats.tagAssignments} 条帖子标签关联`, tone: "blue" },
        { key: "active", label: "成熟标签", value: this.stats.activeTags, hint: "已形成稳定使用规模", tone: "green" },
        { key: "emerging", label: "成长标签", value: this.stats.emergingTags, hint: "正在积累使用量", tone: "amber" },
        { key: "candidate", label: "候选标签", value: this.stats.candidateTags, hint: "后续可考虑合并或保留", tone: "slate" },
        { key: "blocked", label: "已屏蔽", value: this.stats.blockedTags, hint: "不会进入搜索联想", tone: "rose" },
        { key: "relation", label: "标签关系", value: this.stats.relationCount, hint: `${this.stats.aliasCount} 条别名映射`, tone: "purple" }
      ]
    },
    statusTabs() {
      return [
        { value: "", label: "全部", count: this.stats.totalTags },
        { value: "ACTIVE", label: "成熟", count: this.stats.activeTags },
        { value: "EMERGING", label: "成长", count: this.stats.emergingTags },
        { value: "CANDIDATE", label: "候选", count: this.stats.candidateTags },
        { value: "BLOCKED", label: "屏蔽", count: this.stats.blockedTags }
      ]
    }
  },
  created() {
    this.refreshAll()
  },
  methods: {
    async refreshAll() {
      await Promise.all([this.loadOverview(), this.loadTags()])
    },
    async loadOverview() {
      const res = await fetchSquareTagOverview()
      this.stats = { ...emptyStats(), ...(res.data || {}) }
    },
    async loadTags() {
      if (this.loading) return
      this.loading = true
      try {
        const res = await fetchSquareTags({
          keyword: this.filters.keyword || undefined,
          status: this.filters.status || undefined,
          page: this.page,
          pageSize: this.pageSize
        })
        const data = res.data || {}
        this.rows = Array.isArray(data.records) ? data.records : []
        this.total = Number(data.total || 0)
      } catch (error) {
        this.flash(error.message || "标签加载失败", true)
      } finally {
        this.loading = false
      }
    },
    changeStatus(status) {
      this.filters.status = status
      this.page = 1
      this.loadTags()
    },
    search() {
      this.page = 1
      this.loadTags()
    },
    changePage(next) {
      this.page = Math.min(Math.max(1, next), this.totalPages)
      this.loadTags()
    },
    openRename(item) {
      this.renameTag = item
      this.renameValue = item.displayNameOverride || ""
    },
    closeRename() {
      this.renameTag = null
      this.renameValue = ""
    },
    async saveRename() {
      if (!this.renameTag) return
      this.saving = true
      try {
        await updateSquareTagDisplayName(this.renameTag.normalizedName, this.renameValue || null)
        this.flash("标签名称已更新")
        this.closeRename()
        await this.refreshAll()
      } catch (error) {
        this.flash(error.message || "保存失败", true)
      } finally {
        this.saving = false
      }
    },
    async toggleBlocked(item) {
      const blocked = item.moderationStatus === "BLOCKED"
      this.saving = true
      try {
        await updateSquareTagStatus(item.normalizedName, blocked ? "VISIBLE" : "BLOCKED")
        this.flash(blocked ? "标签已恢复" : "标签已屏蔽")
        await this.refreshAll()
      } catch (error) {
        this.flash(error.message || "操作失败", true)
      } finally {
        this.saving = false
      }
    },
    async openMerge(item) {
      this.mergeSource = item
      this.mergeTarget = ""
      try {
        const res = await fetchSquareTags({ page: 1, pageSize: 100 })
        const data = res.data || {}
        this.mergeOptions = (data.records || []).filter(option => option.normalizedName !== item.normalizedName)
      } catch (error) {
        this.mergeOptions = []
      }
    },
    closeMerge() {
      this.mergeSource = null
      this.mergeTarget = ""
      this.mergeOptions = []
    },
    async saveMerge() {
      if (!this.mergeSource || !this.mergeTarget) return
      const target = this.mergeOptions.find(item => item.normalizedName === this.mergeTarget)
      if (!window.confirm(`确认把「${this.mergeSource.displayName}」合并到「${target ? target.displayName : this.mergeTarget}」？`)) return
      this.saving = true
      try {
        await mergeSquareTags(this.mergeSource.normalizedName, this.mergeTarget)
        this.flash("标签已合并")
        this.closeMerge()
        await this.refreshAll()
      } catch (error) {
        this.flash(error.message || "合并失败", true)
      } finally {
        this.saving = false
      }
    },
    async rebuildIndexes() {
      this.rebuilding = true
      try {
        const res = await rebuildSquareTagIndexes()
        this.stats = { ...emptyStats(), ...(res.data || {}) }
        this.flash("标签索引已重建")
        await this.loadTags()
      } catch (error) {
        this.flash(error.message || "重建失败", true)
      } finally {
        this.rebuilding = false
      }
    },
    flash(text, error = false) {
      this.message = text
      this.messageError = error
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("zh-CN")
    },
    formatTime(value) {
      return value ? String(value).replace("T", " ").slice(0, 16) : "暂无"
    }
  }
}
</script>

<style scoped>
.tag-page { display: flex; flex-direction: column; gap: 14px; color: #1f2937; }
.tag-stats { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 10px; }
.tag-stat { padding: 14px; border: 1px solid #e6ebf1; border-radius: 14px; background: #fff; }
.tag-stat span, .tag-stat small { display: block; color: #7b8794; font-size: 10px; }
.tag-stat strong { display: block; margin: 7px 0 5px; font-size: 23px; }
.tag-stat--green strong { color: #17734a; }
.tag-stat--amber strong { color: #a86a00; }
.tag-stat--rose strong { color: #b34435; }
.tag-stat--purple strong { color: #7350a5; }
.tag-card { padding: 16px; border: 1px solid #e8edf3; border-radius: 16px; background: #fff; }
.tag-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.tag-tabs { display: flex; gap: 5px; padding: 4px; overflow-x: auto; border-radius: 11px; background: #f3f6f8; scrollbar-width: none; }
.tag-tabs::-webkit-scrollbar { display: none; }
.tag-tabs button { flex: 0 0 auto; padding: 8px 12px; border: 0; border-radius: 8px; background: transparent; color: #627080; font-size: 12px; cursor: pointer; }
.tag-tabs button.active { background: #fff; color: #17202b; font-weight: 700; box-shadow: 0 2px 8px rgba(15,23,42,.08); }
.tag-tabs em { margin-left: 4px; color: #94a3b8; font-style: normal; font-size: 10px; }
.ghost-button, .primary-button { min-height: 36px; padding: 0 14px; border-radius: 9px; font-size: 12px; cursor: pointer; }
.ghost-button { border: 1px solid #dce3ea; background: #fff; color: #465464; }
.primary-button { border: 0; background: #1b2735; color: #fff; }
.tag-filters { display: flex; gap: 8px; margin: 12px 0; }
.tag-filters input { flex: 1; min-width: 0; height: 38px; padding: 0 11px; border: 1px solid #dce3ea; border-radius: 9px; }
.tag-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(270px, 1fr)); gap: 11px; }
.tag-item { padding: 14px; border: 1px solid #e7ecf2; border-radius: 14px; background: #fff; }
.tag-item--blocked { border-color: #f0d3cb; background: #fffafa; }
.tag-item__head { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.tag-name { overflow: hidden; color: #17202b; font-size: 16px; font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }
.tag-name span { color: #2d7b59; }
.tag-quality { padding: 3px 7px; border-radius: 999px; background: #edf4ff; color: #3965a4; font-size: 9px; }
.tag-normalized { margin-top: 5px; color: #97a2ae; font-size: 10px; }
.tag-metrics { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin: 13px 0 0; }
.tag-metrics div { padding: 8px; border-radius: 9px; background: #f7f9fb; }
.tag-metrics dt { color: #8d99a5; font-size: 9px; }
.tag-metrics dd { margin: 4px 0 0; color: #2f3d4a; font-size: 13px; font-weight: 700; }
.tag-item__foot { display: flex; justify-content: space-between; margin-top: 11px; color: #8b97a3; font-size: 10px; }
.blocked-label { color: #b44332; font-weight: 700; }
.tag-actions { display: flex; gap: 14px; margin-top: 12px; padding-top: 10px; border-top: 1px solid #f0f3f6; }
.tag-actions button { padding: 0; border: 0; background: transparent; color: #256b51; font-size: 11px; cursor: pointer; }
.tag-actions button.danger { color: #b44332; }
.tag-state { padding: 40px 16px; color: #8a96a3; text-align: center; font-size: 12px; }
.tag-message { margin: 0 0 10px; color: #16734a; font-size: 11px; }
.tag-message--error { color: #b44332; }
.pager { display: flex; align-items: center; justify-content: flex-end; gap: 10px; margin-top: 14px; }
.pager-text { color: #7f8b98; font-size: 11px; }
.tag-modal-mask { position: fixed; inset: 0; z-index: 110; display: grid; place-items: center; padding: 16px; background: rgba(15,23,42,.45); }
.tag-modal { width: min(460px, 100%); padding: 18px; border-radius: 16px; background: #fff; }
.tag-modal header { display: flex; align-items: flex-start; justify-content: space-between; }
.tag-modal header small { color: #84909d; font-size: 10px; }
.tag-modal h2 { margin: 5px 0 0; font-size: 18px; }
.tag-modal header button { border: 0; background: transparent; color: #65727f; font-size: 22px; cursor: pointer; }
.tag-modal label { display: block; margin-top: 18px; }
.tag-modal label span { display: block; margin-bottom: 6px; color: #5f6c79; font-size: 11px; }
.tag-modal input, .tag-modal select { width: 100%; height: 40px; padding: 0 10px; border: 1px solid #d9e0e7; border-radius: 9px; }
.tag-modal p { color: #87929e; font-size: 11px; line-height: 1.7; }
.tag-modal footer { display: flex; justify-content: flex-end; gap: 8px; margin-top: 18px; }
@media (max-width: 1100px) { .tag-stats { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 760px) {
  .tag-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
  .tag-stat { padding: 12px; }
  .tag-card { padding: 12px; }
  .tag-toolbar { align-items: stretch; flex-direction: column; }
  .tag-filters { flex-direction: column; }
  .tag-grid { grid-template-columns: 1fr; }
  .tag-modal-mask { padding: 0; align-items: end; }
  .tag-modal { width: 100%; border-radius: 18px 18px 0 0; padding: 18px 16px calc(18px + env(safe-area-inset-bottom)); }
  .tag-modal footer button { flex: 1; }
  .pager { justify-content: center; flex-wrap: wrap; }
}
</style>
